/**
 * /templates — Meta templates CRUD (Story 7.7).
 *
 * Surface:
 *   - Paginated list with status + purpose + active filters.
 *   - "Novo template" button → modal form (create).
 *   - Row actions: edit, sync, deactivate.
 *   - Status is rendered as a color-coded badge:
 *       pending=yellow, approved=green, rejected=red, paused=gray.
 *   - Successful mutations surface as toasts; errors surface the server
 *     message (or a friendly fallback for well-known codes).
 *
 * The table is intentionally light on chrome — no react-table, no virtual
 * scroll. MVP volume is low (< 100 templates per org).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Power, Pencil, Plus, RefreshCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { ApiError, apiClient } from '@/lib/api';
import {
  TemplateForm,
  type TemplateCategory,
  type TemplateFormValue,
  type TemplatePurpose,
  type TemplateStatus,
} from '@/components/templates/TemplateForm';
import type { TemplateVariable } from '@/components/templates/VariablesEditor';

interface TemplateUrlButtonDto {
  type: 'URL';
  text: string;
  url: string;
}

interface TemplateQuickReplyButtonDto {
  type: 'QUICK_REPLY';
  text: string;
  linkedTemplateId: string;
}

/** 2026-08-03 (Story 9.11c) — botão que aciona a IA (texto livre, guiado). */
interface TemplateAiResponseButtonDto {
  type: 'AI_RESPONSE';
  text: string;
  guidanceText: string;
  id: string;
}

type TemplateButtonDto =
  | TemplateUrlButtonDto
  | TemplateQuickReplyButtonDto
  | TemplateAiResponseButtonDto;

interface MetaTemplateDto {
  id: string;
  metaTemplateName: string;
  languageCode: string;
  category: TemplateCategory | null;
  status: TemplateStatus;
  contentPreview: string | null;
  variables: TemplateVariable[];
  /** Story 7.28 — botões URL estáticos (vazio = sem botões). */
  buttons: TemplateButtonDto[];
  purpose: TemplatePurpose;
  active: boolean;
  /** Story 7.25 — true once the template has been submitted to Meta. */
  submittedToMeta: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  data: MetaTemplateDto[];
  page: number;
  pageSize: number;
  total: number;
}

interface Filters {
  status: TemplateStatus | '';
  purpose: TemplatePurpose | '';
  active: '' | 'true' | 'false';
}

const STATUS_BADGE: Record<TemplateStatus, string> = {
  pending: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
  approved: 'bg-green-500/20 text-green-700 dark:text-green-300',
  rejected: 'bg-red-500/20 text-red-700 dark:text-red-300',
  paused: 'bg-muted text-muted-foreground',
};

function StatusBadge({ status }: { status: TemplateStatus }) {
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[status]}`}
    >
      {status}
    </span>
  );
}

interface SyncFromMetaResponse {
  synced: true;
  summary: {
    created: number;
    updated: number;
    unchanged: number;
    total: number;
  };
  at: string;
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as {
      error?: {
        code?: string;
        message?: string;
        campaigns?: unknown[];
        meta_code?: number;
        missing_positions?: number[];
        locked_fields?: string[];
        current_status?: string;
        missing_fields?: string[];
      };
    };
    const code = body?.error?.code;
    if (code === 'TEMPLATE_NAME_TAKEN') {
      return 'Já existe um template com esse nome.';
    }
    if (code === 'TEMPLATE_IN_USE') {
      const count = Array.isArray(body.error?.campaigns) ? (body.error?.campaigns?.length ?? 0) : 0;
      return `Template em uso por ${count} campanha(s) ativa(s).`;
    }
    if (code === 'TEMPLATE_NOT_FOUND') {
      return 'Template não encontrado.';
    }
    if (code === 'META_UPSTREAM_ERROR') {
      const msg = body?.error?.message ?? 'sem detalhe';
      return `Meta retornou erro: ${msg}`;
    }
    if (code === 'META_SUBMIT_FAILED') {
      // Story 7.17 — Meta rejeitou o submit outbound do template.
      const msg = body?.error?.message ?? 'sem detalhe';
      const metaCode = body?.error?.meta_code;
      const suffix = metaCode ? ` (Meta code ${metaCode})` : '';
      return `Meta rejeitou o template: ${msg}${suffix}. O template não foi criado — ajuste e tente novamente.`;
    }
    if (code === 'TEMPLATE_VARIABLES_INVALID') {
      // Story 7.26 — body tem {{N}} sem example correspondente.
      const positions = body?.error?.missing_positions ?? [];
      if (positions.length === 0) {
        return 'Algumas variáveis no body não têm exemplo. Preencha todos os campos de exemplo antes de submeter.';
      }
      const formatted = positions.map((p) => `{{${p}}}`).join(', ');
      const noun = positions.length === 1 ? 'A variável' : 'As variáveis';
      const verb = positions.length === 1 ? 'precisa' : 'precisam';
      return `${noun} ${formatted} ${verb} de exemplo. Preencha o campo "Exemplo" antes de submeter — Meta exige isso para aprovação.`;
    }
    if (code === 'TEMPLATE_BUTTONS_INVALID') {
      // Story 7.28 — botão inválido (texto vazio/>25 ou URL não-https). O form
      // já bloqueia isso inline; esta mensagem é defesa em profundidade.
      const msg = body?.error?.message ?? 'Verifique os botões: texto (1-25) e URL https.';
      return `Botão inválido: ${msg}`;
    }
    if (code === 'TEMPLATE_FIELDS_LOCKED') {
      // Story 7.25 — operador tentou renomear/relanguageiar template já na Meta.
      const fields = body?.error?.locked_fields ?? [];
      const labelMap: Record<string, string> = {
        metaTemplateName: 'nome',
        languageCode: 'idioma',
      };
      const formatted = fields.map((f) => labelMap[f] ?? f).join(' e ');
      return `O ${formatted} de um template só pode ser definido antes do primeiro envio à Meta. Para alterar, crie um novo template.`;
    }
    if (code === 'TEMPLATE_EDIT_BLOCKED') {
      // Story 7.25 — Meta só aceita edits em status approved.
      const current = body?.error?.current_status ?? 'pending';
      const statusLabel: Record<string, string> = {
        pending: 'aguardando aprovação',
        rejected: 'rejeitado',
        paused: 'pausado',
      };
      const label = statusLabel[current] ?? current;
      return `Meta só aceita edição de templates aprovados. Status atual: ${label}. Aguarde a aprovação antes de tentar editar novamente.`;
    }
    if (code === 'META_EDIT_FAILED') {
      // Story 7.25 — Meta rejeitou o submit de edit (4xx).
      const msg = body?.error?.message ?? 'sem detalhe';
      const metaCode = body?.error?.meta_code;
      const suffix = metaCode ? ` (Meta code ${metaCode})` : '';
      return `Meta rejeitou a edição do template: ${msg}${suffix}. As alterações não foram aplicadas — ajuste e tente novamente.`;
    }
    if (code === 'TEMPLATE_INCOMPLETE') {
      // Story 7.25 — submit-on-demand falhou porque faltam campos obrigatórios.
      const missing = body?.error?.missing_fields ?? [];
      const labelMap: Record<string, string> = {
        contentPreview: 'corpo da mensagem',
        category: 'categoria',
      };
      const formatted = missing.map((f) => labelMap[f] ?? f).join(' e ');
      return `Preencha ${formatted} antes de submeter à Meta.`;
    }
    if (body?.error?.message) {
      return body.error.message;
    }
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function toFormValue(t: MetaTemplateDto): TemplateFormValue {
  return {
    metaTemplateName: t.metaTemplateName,
    languageCode: t.languageCode,
    category: t.category,
    status: t.status,
    contentPreview: t.contentPreview,
    variables: t.variables,
    buttons: t.buttons.map((b) => {
      if (b.type === 'QUICK_REPLY') {
        return { type: 'QUICK_REPLY' as const, text: b.text, linkedTemplateId: b.linkedTemplateId };
      }
      if (b.type === 'AI_RESPONSE') {
        // `id` round-tripa pro form (ver TemplateAiResponseButtonValue) —
        // preserva o id já atribuído pelo backend entre edições.
        return {
          type: 'AI_RESPONSE' as const,
          text: b.text,
          guidanceText: b.guidanceText,
          id: b.id,
        };
      }
      return { type: 'URL' as const, text: b.text, url: b.url };
    }),
    purpose: t.purpose,
  };
}

export function TemplatesPage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<Filters>({ status: '', purpose: '', active: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MetaTemplateDto | null>(null);

  const listQuery = useQuery({
    queryKey: ['templates', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('pageSize', '50');
      if (filters.status) params.set('status', filters.status);
      if (filters.purpose) params.set('purpose', filters.purpose);
      if (filters.active) params.set('active', filters.active);
      return apiClient.get<ListResponse>(`/admin/templates?${params.toString()}`);
    },
  });

  const createMutation = useMutation({
    mutationFn: (body: TemplateFormValue) =>
      apiClient.post<MetaTemplateDto>('/admin/templates', body),
    onSuccess: () => {
      toast.success('Template criado.');
      setCreateOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao criar template.'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Partial<TemplateFormValue> }) =>
      apiClient.put<MetaTemplateDto>(`/admin/templates/${id}`, body),
    onSuccess: () => {
      toast.success('Template atualizado.');
      setEditTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao atualizar template.'));
    },
  });

  const syncMutation = useMutation({
    mutationFn: (id: string) =>
      apiClient.post<{ synced: boolean; status: TemplateStatus; template: MetaTemplateDto }>(
        `/admin/templates/${id}/sync`,
      ),
    onSuccess: (res) => {
      // Story 7.25 — sync can now mean "submit local-only template to Meta"
      // OR "pull latest status". The response template tells us which path
      // ran via `submittedToMeta`.
      if (res.template.submittedToMeta && res.status === 'pending') {
        toast.success('Template enviado à Meta — aguardando aprovação.');
      } else {
        toast.success(`Sincronizado com Meta (status: ${res.status}).`);
      }
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao sincronizar com Meta.'));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete<MetaTemplateDto>(`/admin/templates/${id}`),
    onSuccess: () => {
      toast.success('Template desativado.');
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao desativar template.'));
    },
  });

  // Story 7.15 — bulk sync from Meta. Pulls every template on the WABA and
  // upserts locally; preserves operator-owned attributes (purpose, variables).
  const syncFromMetaMutation = useMutation({
    mutationFn: () => apiClient.post<SyncFromMetaResponse>('/admin/templates/sync-from-meta'),
    onSuccess: (res) => {
      const { created, updated, unchanged } = res.summary;
      toast.success(
        `Sincronização concluída: ${created} novos, ${updated} atualizados, ${unchanged} inalterados.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao sincronizar com Meta.'));
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Templates</h1>
          <p className="text-sm text-muted-foreground">
            Templates aprovados na Meta com suas variáveis e propósito.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => syncFromMetaMutation.mutate()}
            disabled={syncFromMetaMutation.isPending}
            aria-label="Sincronizar com Meta"
          >
            <RefreshCcw
              className={`mr-1 ${syncFromMetaMutation.isPending ? 'animate-spin' : ''}`}
              aria-hidden
            />
            {syncFromMetaMutation.isPending ? 'Sincronizando…' : 'Sincronizar com Meta'}
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1" aria-hidden />
            Novo template
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <select
              aria-label="Filtro status"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value as TemplateStatus | '' })
              }
            >
              <option value="">Todos status</option>
              <option value="pending">pending</option>
              <option value="approved">approved</option>
              <option value="rejected">rejected</option>
              <option value="paused">paused</option>
            </select>
            <select
              aria-label="Filtro purpose"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.purpose}
              onChange={(e) =>
                setFilters({ ...filters, purpose: e.target.value as TemplatePurpose | '' })
              }
            >
              <option value="">Todos propósitos</option>
              <option value="initial_contact">initial_contact</option>
              <option value="callback_resume">callback_resume</option>
              <option value="reengagement">reengagement</option>
              <option value="converted">converted</option>
            </select>
            <select
              aria-label="Filtro active"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.active}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  active: e.target.value as '' | 'true' | 'false',
                })
              }
            >
              <option value="">Ativos + inativos</option>
              <option value="true">Apenas ativos</option>
              <option value="false">Apenas inativos</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
          ) : listQuery.error ? (
            <div className="p-6 text-sm text-destructive">
              {humanizeError(listQuery.error, 'Falha ao carregar templates.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Nome</th>
                    <th className="px-4 py-2">Language</th>
                    <th className="px-4 py-2">Categoria</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Propósito</th>
                    <th className="px-4 py-2">Ativo</th>
                    <th className="px-4 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {listQuery.data && listQuery.data.data.length > 0 ? (
                    listQuery.data.data.map((t) => (
                      <tr key={t.id} className="border-b last:border-b-0">
                        <td className="px-4 py-2 font-mono text-xs">
                          <div className="flex items-center gap-2">
                            <span>{t.metaTemplateName}</span>
                            {/* Story 7.25 — flag templates that exist only locally so the
                                operator knows the Sync button will SUBMIT, not pull. */}
                            {!t.submittedToMeta ? (
                              <span
                                data-testid={`local-only-badge-${t.id}`}
                                className="rounded border border-amber-400/40 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-400"
                                title="Template ainda não enviado à Meta — use o botão Sincronizar para submetê-lo."
                              >
                                Local
                              </span>
                            ) : null}
                          </div>
                          {/* Story 7.28 — exibe os botões URL configurados (AC5). */}
                          {t.buttons.length > 0 ? (
                            <div
                              className="mt-1 flex flex-wrap gap-1"
                              data-testid={`template-buttons-${t.id}`}
                            >
                              {t.buttons.map((b, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center rounded border border-input bg-muted px-1.5 py-0.5 text-[10px] font-medium"
                                  title={
                                    b.type === 'URL'
                                      ? b.url
                                      : b.type === 'QUICK_REPLY'
                                        ? `Template: ${b.linkedTemplateId}`
                                        : `Resposta da IA: ${b.guidanceText}`
                                  }
                                >
                                  {b.type === 'URL' ? '🔗' : b.type === 'QUICK_REPLY' ? '💬' : '🤖'}{' '}
                                  {b.text}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-2">{t.languageCode}</td>
                        <td className="px-4 py-2">{t.category ?? '—'}</td>
                        <td className="px-4 py-2">
                          <StatusBadge status={t.status} />
                        </td>
                        <td className="px-4 py-2">{t.purpose}</td>
                        <td className="px-4 py-2">{t.active ? 'Sim' : 'Não'}</td>
                        <td className="px-4 py-2">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditTarget(t)}
                              aria-label={`Editar ${t.metaTemplateName}`}
                            >
                              <Pencil aria-hidden />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={syncMutation.isPending}
                              onClick={() => syncMutation.mutate(t.id)}
                              aria-label={
                                t.submittedToMeta
                                  ? `Atualizar status de ${t.metaTemplateName}`
                                  : `Submeter ${t.metaTemplateName} à Meta`
                              }
                              title={
                                t.submittedToMeta
                                  ? 'Atualizar status do template a partir da Meta'
                                  : 'Submeter este template à Meta (ainda não enviado)'
                              }
                            >
                              <RefreshCw aria-hidden />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!t.active || deactivateMutation.isPending}
                              onClick={() => deactivateMutation.mutate(t.id)}
                              aria-label={`Desativar ${t.metaTemplateName}`}
                            >
                              <Power aria-hidden />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhum template encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo template</DialogTitle>
            <DialogDescription>
              Preencha os dados para registrar um template Meta.
            </DialogDescription>
          </DialogHeader>
          <TemplateForm
            mode="create"
            submitting={createMutation.isPending}
            availableTemplates={listQuery.data?.data.map((t) => ({
              id: t.id,
              metaTemplateName: t.metaTemplateName,
            }))}
            onCancel={() => setCreateOpen(false)}
            onSubmit={(next) => createMutation.mutate(next)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editTarget !== null} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar template</DialogTitle>
            <DialogDescription>O nome Meta não pode ser alterado após a criação.</DialogDescription>
          </DialogHeader>
          {editTarget ? (
            <TemplateForm
              mode="edit"
              submitting={updateMutation.isPending}
              submittedToMeta={editTarget.submittedToMeta}
              initial={toFormValue(editTarget)}
              availableTemplates={listQuery.data?.data.map((t) => ({
                id: t.id,
                metaTemplateName: t.metaTemplateName,
              }))}
              currentTemplateId={editTarget.id}
              onCancel={() => setEditTarget(null)}
              onSubmit={(next) => {
                // Never ship the name on an edit (server locks it anyway).
                // Also drop languageCode when the template is already on Meta —
                // server would 422 (TEMPLATE_FIELDS_LOCKED) if it changed.
                const { metaTemplateName: _ignoredName, languageCode, ...rest } = next;
                void _ignoredName;
                const body = editTarget.submittedToMeta ? rest : { ...rest, languageCode };
                updateMutation.mutate({ id: editTarget.id, body });
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TemplatesPage;
